import {
  Decoration,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  WidgetType,
  keymap,
  lineNumbers as lineNumbersExt,
  placeholder as placeholderExt,
  type DecorationSet,
  type ViewUpdate,
} from "@codemirror/view";
import { EditorState, type Extension } from "@codemirror/state";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import {
  bracketMatching,
  indentOnInput,
  syntaxHighlighting,
  defaultHighlightStyle,
} from "@codemirror/language";
import {
  autocompletion,
  closeBrackets,
  closeBracketsKeymap,
  completionKeymap,
} from "@codemirror/autocomplete";
import { linter, type Diagnostic } from "@codemirror/lint";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { sql, PostgreSQL } from "@codemirror/lang-sql";
import { codeEditorTheme } from "./codeEditorTheme";

export type CodeEditorLanguage = "json" | "sql" | "markdown";

export interface CodeEditorDiagnostic {
  line: number;
  column: number;
  endColumn?: number;
  severity: "error" | "warning" | "info" | "hint";
  message: string;
}

export interface CodeEditorKeyBinding {
  key: string;
  run: (value: string) => boolean;
}

export interface BuildExtensionsOptions {
  language: CodeEditorLanguage;
  lineNumbers: boolean;
  readOnly: boolean;
  placeholder?: string;
  formatOnPaste?: "json";
  keymap?: CodeEditorKeyBinding[];
  onBlur?: (value: string) => void;
  isDark: boolean;
  enableAutocomplete: boolean;
  lineWrapping: boolean;
  /**
   * Draw `{{name}}` as a chip rather than as literal braces. For text that
   * describes a shape rather than a statement — a recipe's definition, where a
   * slot and a parameter are both holes the reader will fill.
   */
  slotTokens?: boolean;
}

/** The chip itself: the name alone, since the box already says it is a hole. */
class SlotTokenWidget extends WidgetType {
  constructor(private readonly name: string) {
    super();
  }
  eq(other: SlotTokenWidget): boolean {
    return other.name === this.name;
  }
  toDOM(): HTMLElement {
    const chip = document.createElement("span");
    chip.className = "cm-slot-chip";
    chip.textContent = this.name;
    return chip;
  }
  ignoreEvent(): boolean {
    return false;
  }
}

const slotTokenDecorator = new MatchDecorator({
  regexp: /\{\{([A-Za-z_][A-Za-z0-9_]*)\}\}/g,
  decoration: (match) =>
    Decoration.replace({ widget: new SlotTokenWidget(match[1] ?? "") }),
});

const slotTokenPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = slotTokenDecorator.createDeco(view);
    }
    update(update: ViewUpdate) {
      this.decorations = slotTokenDecorator.updateDeco(
        update,
        this.decorations,
      );
    }
  },
  { decorations: (plugin) => plugin.decorations },
);

/**
 * Convert a 1-indexed (line, column) marker — the Postgres convention used by
 * our SQL backend and likely any future caller — into a CM6 absolute offset
 * Diagnostic. Out-of-range positions are clamped so the underline always
 * renders somewhere visible instead of silently disappearing.
 */
export function diagnosticToCM6(
  view: EditorView,
  d: CodeEditorDiagnostic,
): Diagnostic {
  const doc = view.state.doc;
  const totalLines = doc.lines;
  // Trust-boundary defense: a malicious or buggy backend can send NaN, -1,
  // or huge numbers. Guard with Number.isFinite before clamping — otherwise
  // doc.line(NaN) throws and aborts the entire setDiagnostics transaction.
  const safeLine = Number.isFinite(d.line) ? d.line : 1;
  const safeCol = Number.isFinite(d.column) ? d.column : 1;
  const line = Math.max(1, Math.min(totalLines, safeLine));
  const lineInfo = doc.line(line);
  const from =
    lineInfo.from + Math.max(0, Math.min(lineInfo.length, safeCol - 1));
  // Default endColumn is column+1 (one-char underline). Also enforce
  // endColumn > column so a malformed marker (endColumn <= column) still
  // renders a multi-char-intent as at least one character wide rather than
  // collapsing to zero.
  const rawEndCol = Number.isFinite(d.endColumn)
    ? (d.endColumn as number)
    : safeCol + 1;
  const endCol = Math.max(rawEndCol, safeCol + 1);
  const to = lineInfo.from + Math.max(0, Math.min(lineInfo.length, endCol - 1));
  return {
    from,
    to: Math.max(to, from + 1),
    severity: d.severity,
    message: d.message,
  };
}

/*
 * Paste handler that pretty-prints valid JSON via JSON.parse/stringify.
 * Falls back to default paste behavior on parse failure — same semantics as
 * Monaco's `formatOnPaste: true` for the JSON language.
 *
 * Caveats (intentional — fall through to default paste, do not format):
 *  - Above PASTE_FORMAT_MAX_BYTES, parsing+stringifying blocks the main
 *    thread long enough to be a DoS vector. Hard cap, no formatting.
 *  - JSON.parse/stringify quietly loses precision on integers > 2^53 and
 *    converts +/-Infinity to null. For JSON cells with `id` columns pasted
 *    from external systems, this matters. The fall-through preserves raw
 *    text so the user keeps what they pasted; we only reformat when round-
 *    tripping is value-preserving for typical payloads.
 */
const PASTE_FORMAT_MAX_BYTES = 256 * 1024; // 256 KB

const jsonPasteFormatter = EditorView.domEventHandlers({
  paste(event, view) {
    const text = event.clipboardData?.getData("text/plain");
    if (!text || text.length > PASTE_FORMAT_MAX_BYTES) return false;
    try {
      const parsed = JSON.parse(text);
      const formatted = JSON.stringify(parsed, null, 2);
      const sel = view.state.selection.main;
      event.preventDefault();
      view.dispatch({
        changes: { from: sel.from, to: sel.to, insert: formatted },
        selection: { anchor: sel.from + formatted.length },
      });
      return true;
    } catch {
      return false;
    }
  },
});

/*
 * Defensive blur handler that ignores blur events caused by focus moving into
 * an editor-owned tooltip (autocomplete or lint hover). Future-proof guard —
 * current usage doesn't intersect blur-commit with autocomplete, but if anyone
 * later enables autocomplete on the JSON editor, this prevents stale commits
 * when the user clicks a suggestion.
 */
function blurHandler(onBlur: (value: string) => void) {
  return EditorView.domEventHandlers({
    blur(event, view) {
      const related = event.relatedTarget as Element | null;
      if (
        related &&
        (related.closest(".cm-tooltip") || view.dom.contains(related))
      ) {
        return false;
      }
      onBlur(view.state.doc.toString());
      return false;
    },
  });
}

function languageExtension(lang: CodeEditorLanguage): Extension {
  switch (lang) {
    case "json":
      return json();
    case "sql":
      return sql({ dialect: PostgreSQL, upperCaseKeywords: false });
    case "markdown":
      return markdown();
  }
}

export function buildExtensions(opts: BuildExtensionsOptions): Extension[] {
  const exts: Extension[] = [
    history(),
    indentOnInput(),
    bracketMatching(),
    closeBrackets(),
    syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
    ...codeEditorTheme(opts.isDark),
    languageExtension(opts.language),
  ];

  if (opts.lineNumbers) {
    exts.push(lineNumbersExt());
  }

  if (opts.placeholder !== undefined && opts.placeholder !== "") {
    exts.push(placeholderExt(opts.placeholder));
  }

  if (opts.readOnly) {
    exts.push(EditorState.readOnly.of(true));
  }

  // A replace decoration hides the braces, so the document and what is drawn
  // differ — fine while nobody can type, which is the only way this is offered.
  if (opts.slotTokens && opts.readOnly) {
    exts.push(slotTokenPlugin);
  }

  if (opts.formatOnPaste === "json") {
    exts.push(jsonPasteFormatter);
  }

  if (opts.lineWrapping) {
    exts.push(EditorView.lineWrapping);
  }

  if (opts.enableAutocomplete) {
    exts.push(autocompletion());
  }

  // Pass `null` for the source: registers the lint state field + the
  // decoration view plugin (so underlines and hover tooltips render) without
  // a polling source that would race with React-driven setDiagnostics calls
  // and silently wipe them.
  exts.push(linter(null));

  // User keymap goes BEFORE the default keymap so user bindings win on
  // conflicting keys (e.g. Cmd-Enter should fire onCommit, not default newline).
  const userKeymapEntries = (opts.keymap ?? []).map((kb) => ({
    key: kb.key,
    run: (view: EditorView) => kb.run(view.state.doc.toString()),
    preventDefault: true,
  }));

  exts.push(
    keymap.of([
      ...userKeymapEntries,
      ...closeBracketsKeymap,
      ...defaultKeymap,
      ...historyKeymap,
      ...completionKeymap,
    ]),
  );

  if (opts.onBlur) {
    exts.push(blurHandler(opts.onBlur));
  }

  return exts;
}
